import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Badge, 
  Spinner, 
  Alert, 
  Row, 
  Col, 
  Card,
  Modal,
  Pagination
} from 'react-bootstrap';
import axios from 'axios';

const ChatHistory = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalChats, setTotalChats] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [limit] = useState(10);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchChats();
  }, [currentPage]);

  const fetchChats = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * limit;
              const url = `/api/ai/chats?limit=${limit}&offset=${offset}`;
      
      const response = await axios.get(url);
      
      // Sort chats by creation date (latest first)
      const sortedChats = response.data.chats.sort((a, b) => {
        const dateA = a.metadata?.createdAt || a.startedAt;
        const dateB = b.metadata?.createdAt || b.startedAt;
        return new Date(dateB) - new Date(dateA);
      });
      
      setChats(sortedChats);
      setTotalChats(response.data.total);
      setTotalPages(Math.ceil(response.data.total / limit));
    } catch (error) {
      setError('Failed to fetch chat history: ' + (error.response?.data?.detail || error.message));
      console.error('Error fetching chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTranscript = (chat) => {
    setSelectedChat(chat);
    setShowTranscript(true);
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': { variant: 'success', text: 'Completed' },
      'in-progress': { variant: 'warning', text: 'In Progress' },
      'failed': { variant: 'danger', text: 'Failed' },
      'cancelled': { variant: 'secondary', text: 'Cancelled' }
    };
    
    const config = statusConfig[status] || { variant: 'info', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };



  const syncWithVAPI = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      
              const response = await axios.post('/api/ai/smart-sync-vapi');
      
      setSyncResult(response.data.result);
      
      // Refresh the chat list after sync
      await fetchChats();
      
    } catch (error) {
      setError('Failed to sync with VAPI: ' + (error.response?.data?.detail || error.message));
      console.error('Error syncing with VAPI:', error);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <Row>
          <Col>
            <h2>Chat History</h2>
            <p className="text-muted mb-0">View chat conversations and transcripts from VAPI</p>
          </Col>
        </Row>
      </div>

      <div className="page-content">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {syncResult && (
          <Alert variant="info" onClose={() => setSyncResult(null)} dismissible>
            <h6>VAPI Sync Results:</h6>
            <ul className="mb-0">
              <li>New chats: {syncResult.chats_new || 0}</li>
              <li>Updated chats: {syncResult.chats_updated || 0}</li>
              <li>Total chats in VAPI: {syncResult.total_chats_in_vapi || 0}</li>
            </ul>
          </Alert>
        )}

        <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Chats ({totalChats})
              {syncing && (
                <span className="ms-2">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span className="ms-1 text-muted">Syncing...</span>
                </span>
              )}
            </h5>
            <div>
              <Button 
                variant="outline-success" 
                size="sm"
                className="me-2"
                onClick={syncWithVAPI}
                disabled={syncing || loading}
              >
                {syncing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Sync VAPI
                  </>
                ) : (
                  '🔄 Sync VAPI'
                )}
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={fetchChats}
                disabled={loading || syncing}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Refresh'
                )}
              </Button>
            </div>
          </div>

          <Table responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer</th>
                <th>Project ID</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {chats.map((chat) => (
                <tr key={chat.id}>
                  <td>
                    <code>{chat.id}</code>
                  </td>
                  <td>
                    <div>
                      <strong>{chat.customer?.name || 'Unknown'}</strong>
                      <br />
                      <small className="text-muted">
                        {chat.customer?.phoneNumber || 'No phone'}
                      </small>
                    </div>
                  </td>
                  <td>
                    <code>{chat.assistantId}</code>
                  </td>
                  <td>{getStatusBadge(chat.status)}</td>
                  <td>{chat.metadata?.createdAt ? formatDate(chat.metadata.createdAt) : formatDate(chat.startedAt)}</td>
                  <td>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewTranscript(chat)}
                    >
                      View Chat
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First 
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
      </div>

      {/* Transcript Modal */}
      <Modal show={showTranscript} onHide={() => setShowTranscript(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Chat Conversation - Assistant ID: {selectedChat?.assistantId || 'Unknown'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedChat && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Assistant ID:</strong> <code>{selectedChat.assistantId || 'Unknown'}</code>
                </Col>
                <Col md={6}>
                  <strong>Status:</strong> {getStatusBadge(selectedChat.status)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Created:</strong> {selectedChat.metadata?.createdAt ? formatDate(selectedChat.metadata.createdAt) : formatDate(selectedChat.startedAt)}
                </Col>
                <Col md={6}>
                  <strong>Updated:</strong> {selectedChat.metadata?.updatedAt ? formatDate(selectedChat.metadata.updatedAt) : 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Cost:</strong> ${selectedChat.metadata?.cost || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Created:</strong> {selectedChat.metadata?.createdAt ? formatDate(selectedChat.metadata.createdAt) : 'N/A'}
                </Col>
              </Row>
              
              <div className="mt-4">
                <h6>Chat Conversation:</h6>
                <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {selectedChat.metadata && (
                    <div>
                      {/* Show input messages */}
                      {selectedChat.metadata.input && Array.isArray(selectedChat.metadata.input) && (
                        <div>
                          <h6 className="text-muted mb-2">Input Messages:</h6>
                          {selectedChat.metadata.input.map((message, index) => (
                            <div key={`input-${index}`} className={`mb-2 p-2 rounded ${message.role === 'user' ? 'bg-light' : 'bg-primary text-white'}`}>
                              <small className="text-muted">{message.role === 'user' ? 'User' : 'AI Assistant'}</small>
                              <div>{message.content}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Show output messages */}
                      {selectedChat.metadata.output && Array.isArray(selectedChat.metadata.output) && (
                        <div className="mt-3">
                          <h6 className="text-muted mb-2">Output Messages:</h6>
                          {selectedChat.metadata.output.map((message, index) => (
                            <div key={`output-${index}`} className={`mb-2 p-2 rounded ${message.role === 'user' ? 'bg-light' : 'bg-primary text-white'}`}>
                              <small className="text-muted">{message.role === 'user' ? 'User' : 'AI Assistant'}</small>
                              <div>{message.content}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Fallback if no structured messages */}
                      {(!selectedChat.metadata.input || !selectedChat.metadata.output) && (
                        <div className="text-muted">
                          No chat messages available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTranscript(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChatHistory; 